import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Modal,
  Form,
  Cascader,
  Input,
  DatePicker,
  Checkbox,
  Button,
  Space,
  message,
  Select,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import useService from '@/lib/hooks/useService';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import styles from './index.module.scss';
import type { SingleValueType } from 'rc-cascader/lib/Cascader';
import type { ResidentAccountNumber as Resident } from '@/types/database/residentAccountNumbers';
import type { Option as OptionType } from '@/utils/generateHouseHoldNumberCascader';
import type { CarServiceReservation } from '@/types/database/carService';
import type { CommunityData, FormDataType } from '../../types';
import type { User } from '@/types/database/users';

interface AddReservationModalProps {
  isOpenModal: boolean;
  closeModal: () => void;
  updateReservations: () => void;
}

dayjs.extend(advancedFormat);

const { TextArea } = Input;

const selectorStyle = {
  width: '200px',
};

const AddReservationModal = ({
  isOpenModal,
  closeModal,
  updateReservations,
}: AddReservationModalProps) => {
  const [residents] = useService<Resident>({
    collectionName: 'residentAccountNumbers',
  });
  const [allReservations, , carService] = useService<CarServiceReservation>({
    collectionName: 'carService',
  });
  const [communityData] = useService<CommunityData>({
    collectionName: 'communityData',
  });
  const [users] = useService<User>({
    collectionName: 'users',
  });

  const [form] = Form.useForm<FormDataType>();

  const [selectedHousehold, setSelectedHousehold] = useState<SingleValueType>(
    []
  );

  const householdNumbers = residents.map(
    (resident) => resident.householdNumber
  );

  const hasMembersHouseholdNumbers = useMemo(() => {
    return residents.reduce<string[]>((passedHouseholds, resident) => {
      if (resident.members && resident.members.length > 0) {
        passedHouseholds.push(resident.householdNumber);
      }
      return passedHouseholds;
    }, []);
  }, [residents]);

  const cascaderOptions = generateHouseHoldNumberCascader(householdNumbers);

  const compareNumber = (option: OptionType) => {
    if (!option.children || option.children.length === 0) {
      return;
    }
    option.children.sort((a: OptionType, b: OptionType) => {
      if (parseInt(a.value) - parseInt(b.value) > 0) {
        return 1;
      } else if (parseInt(a.value) - parseInt(b.value) < 0) {
        return -1;
      } else return 0;
    });

    option.children.forEach((childOption) => {
      compareNumber(childOption);
    });
  };

  cascaderOptions.forEach((option) => {
    compareNumber(option);
  });

  const handleFormValuesChange = (changedValues: FormDataType) => {
    if ('household' in changedValues) {
      handleHouseholdNumberChange(changedValues.household);
    }
  };

  const handleHouseholdNumberChange = (household: SingleValueType) => {
    form.setFieldValue('resident', []);
    setSelectedHousehold(household);
  };

  const displayRender = (label: string[]) => {
    return label.join('-');
  };

  const getReservationOrderNumber = (reservationTime: string) => {
    const reservationDate = dayjs(reservationTime).format('YYYYMMDD');
    const reservationsLength = allReservations.filter((reservation) => {
      return (
        dayjs(reservation.reservationTime).format('YYYYMMDD') ===
        reservationDate
      );
    }).length;
    const reservationOrder = (reservationsLength + 1)
      .toString()
      .padStart(3, '0');
    return reservationDate + reservationOrder;
  };

  const handleSubmit = async (values: FormDataType) => {
    const {
      resident,
      destination,
      reservationTime,
      residentPhone,
      specialRequests,
      additionalRequests,
      needCare,
    } = values;

    const selectedResident = users.find((user) => {
      return user.name === resident;
    });

    const submittedData: CarServiceReservation = {
      fleetName: '',
      carNumber: '',
      reservationTime: Number(dayjs(reservationTime).format('x')),
      actualCarTime: '',
      estimatedArrivalTime: '',
      actualArrivalTime: '',
      pickupLocation: communityData[0]?.name,
      destination,
      driverName: '',
      driverPhone: '',
      residentPhone,
      orderNumber: getReservationOrderNumber(reservationTime),
      specialRequests,
      additionalRequests,
      status: '已預約',
      needCare,
      userId: selectedResident?._id ?? '',
    };

    await carService.create(submittedData).then(() => {
      message.success('叫車成功！');
      cancelForm();
      updateReservations();
    });
  };

  const cancelForm = () => {
    form.resetFields();
    setSelectedHousehold([]);
    closeModal();
  };

  const displayTag = () => {
    return !selectedHousehold ||
      selectedHousehold.length ===
        0 ? null : hasMembersHouseholdNumbers.includes(
        selectedHousehold?.join('-')
      ) ? (
      <Tag color="green">已開通</Tag>
    ) : (
      <Tag color="red">未開通</Tag>
    );
  };

  const findResidentByHousehold = useCallback(
    (selectedHousehold) => {
      return users.reduce(
        (
          usersBelongSelectedHousehold: { label: string; value: string }[],
          user
        ) => {
          if (
            user.householdNumbers &&
            user.householdNumbers[0] === selectedHousehold.join('-')
          ) {
            usersBelongSelectedHousehold.push({
              label: user.name,
              value: user.name,
            });
          }
          return usersBelongSelectedHousehold;
        },
        []
      );
    },
    [users]
  );

  const getSelectResidentOptions = useCallback(
    (selectedHousehold) => {
      if (!selectedHousehold) {
        return [];
      }
      const foundResident = residents.find(
        (resident) => resident.householdNumber === selectedHousehold.join('-')
      );

      if (foundResident?.members && foundResident.members.length !== 0) {
        return foundResident.members.map((member) => ({
          label: member,
          value: member,
        }));
      } else {
        return findResidentByHousehold(selectedHousehold);
      }
    },
    [findResidentByHousehold, residents]
  );

  return (
    <Modal
      open={isOpenModal}
      closable={false}
      onCancel={closeModal}
      footer={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormValuesChange}
      >
        <Space align="start" size={60}>
          <Form.Item
            label={<span className={styles.householdLabel}>戶號</span>}
          >
            <Form.Item
              name="household"
              rules={[{ required: true, message: '請選擇戶號' }]}
              initialValue=""
            >
              <Cascader
                style={selectorStyle}
                options={cascaderOptions}
                placeholder="請選擇戶號"
                displayRender={displayRender}
              />
            </Form.Item>
            <div className={styles.tagContainer}>{displayTag()}</div>
          </Form.Item>
          <Form.Item
            name="resident"
            label="住戶"
            rules={[{ required: true, message: '請選擇住戶' }]}
          >
            <Select
              style={selectorStyle}
              placeholder="請選擇住戶"
              options={getSelectResidentOptions(selectedHousehold)}
            />
          </Form.Item>
        </Space>
        <Form.Item name="destination" label="下車地點" initialValue="">
          <Input placeholder="請輸入下車地點" />
        </Form.Item>
        <Form.Item
          name="reservationTime"
          label="搭車時間"
          rules={[{ required: true, message: '請選擇搭車時間' }]}
          initialValue=""
        >
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            inputReadOnly
            style={selectorStyle}
          />
        </Form.Item>
        <Form.Item
          name="residentPhone"
          label="聯絡電話"
          rules={[{ required: true, message: '請輸入聯絡電話' }]}
          initialValue=""
        >
          <Input placeholder="請輸入聯絡電話" />
        </Form.Item>
        <Form.Item name="specialRequests" label="特殊需求" initialValue="">
          <TextArea
            rows={4}
            placeholder="請輸入特殊需求"
            maxLength={100}
            showCount
          />
        </Form.Item>
        <Form.Item
          name="needCare"
          label="其他"
          initialValue={false}
          valuePropName="checked"
        >
          <Checkbox>下車關懷服務</Checkbox>
        </Form.Item>
        <Form.Item name="additionalRequests" label="備註" initialValue="">
          <TextArea
            rows={4}
            placeholder="請輸入備註"
            maxLength={100}
            showCount
          />
        </Form.Item>
        <Form.Item>
          <Space className={styles.floatRight}>
            <Button onClick={cancelForm}>取消</Button>
            <Button type="primary" htmlType="submit">
              確定
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(AddReservationModal);
